import { useCallback, useId } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { UploadIcon, XIcon } from 'lucide-react';
import { FileUploaderProps, FileListProps } from './FileUploader.types';
import {
  KNOWLEDGE_BASE_ALLOWED_EXTENSIONS,
  KNOWLEDGE_BASE_SHOWCASED_FORMATS,
} from '@/shared/constants/file-upload.constants';
import { KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES } from '@inference.engine/file-utils/web';
import { KnowledgeBaseFormField } from '@/features/knowledge-bases/ui/KnowledgeBaseModal/KnowledgeBaseFormField';
import { Dropzone, FileTag } from '@/shared/ui';
import { useFileSelection } from './useFileSelection';
import { useTranslation } from 'react-i18next';

const FileList = ({ files, onRemove }: FileListProps) => {
  const { t } = useTranslation('knowledge');

  if (files.length === 0) return null;

  return (
    <ul className="mt-4 flex flex-row flex-wrap gap-2">
      {files.map((file, index) => (
        <li key={`${file.name}-${index}`}>
          <FileTag
            filename={file.name}
            size={file.size}
            fileType={file.type}
            endAdornment={
              <Button
                variant="ghost"
                size="icon-mini"
                aria-label={t('knowledgeBases.fileUploader.removeFile', { fileName: file.name })}
                onClick={() => onRemove(index)}
              >
                <XIcon className="size-3.5" />
              </Button>
            }
          />
        </li>
      ))}
    </ul>
  );
};

export const FileUploader = ({ open, onClose, title, onAddDocuments }: FileUploaderProps) => {
  const { t } = useTranslation('knowledge');

  const fieldId = useId();

  const { files, errors, handleDrop, handleAddDocuments, clearFiles, removeFile } =
    useFileSelection({
      onAddDocuments,
      allowedExtensions: KNOWLEDGE_BASE_ALLOWED_EXTENSIONS,
    });

  const handleClose = useCallback(() => {
    clearFiles();
    onClose();
  }, [clearFiles, onClose]);

  const handleSubmit = useCallback(async () => {
    handleClose();
    await handleAddDocuments();
  }, [handleClose, handleAddDocuments]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <KnowledgeBaseFormField
            icon={<UploadIcon />}
            required
            title={t('knowledgeBases.fileUploader.uploadDocument')}
            description={t('knowledgeBases.fileUploader.ourIntelligenceWillAnalyzeAnd')}
            descriptionId={`${fieldId}-description`}
          >
            <Dropzone
              onDrop={handleDrop}
              acceptedFormats={KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES}
              showcasedFormats={KNOWLEDGE_BASE_SHOWCASED_FORMATS}
            />

            <FileList files={files} onRemove={removeFile} />
          </KnowledgeBaseFormField>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('knowledgeBases.fileUploader.cancel')}
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={files.length === 0 || errors.length > 0}
          >
            {t('knowledgeBases.fileUploader.addDocuments')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
